import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Slideout, TextField, Typography, Icon, Scrollbar, CircularProgress } from '@ann/web-components';
import {
  SlideoutContainer,
  SlideoutHeader,
  SlideoutSubHeader,
  SlideoutDrivesContainer,
} from './SlideoutDriveDetail.styled';
import { DriveDocumentList } from './components';
import { listDocuments } from '../../../../../../../services/document';

const SlideoutDriveDetail = ({ open, onClose, drive }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch documentos del drive cuando se abre
  useEffect(() => {
    if (!open || !drive) return;

    const fetchDriveDocuments = async () => {
      const vaultUuid = drive.vaultUuid || drive.uuid || drive.id;
      if (!vaultUuid) {
        setError('Drive UUID not available');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await listDocuments({
          vault_uuid: vaultUuid,
          page_size: 100,
        });

        const documentsList = response.data?.data || [];
        setDocuments(documentsList);
      } catch (err) {
        console.error('Error fetching drive documents:', err);
        setError(err.message || 'Error loading documents');
      } finally {
        setLoading(false);
      }
    };

    fetchDriveDocuments();
  }, [open, drive]);

  // Reset cuando se cierra
  useEffect(() => {
    if (!open) {
      setDocuments([]);
      setSearchTerm('');
      setError(null);
    }
  }, [open]);

  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
  }, []);

  // Filtrar documentos por búsqueda
  const filteredDocuments = useMemo(() => {
    if (!searchTerm.trim()) return documents;

    const searchLower = searchTerm.toLowerCase();
    return documents.filter((doc) =>
      doc.name?.toLowerCase().includes(searchLower) ||
      doc.description?.toLowerCase().includes(searchLower) ||
      doc.type?.toLowerCase().includes(searchLower)
    );
  }, [documents, searchTerm]);

  if (!drive) return null;

  return (
    <Slideout onClose={onClose} size="medium" open={open}>
      <Slideout.HeaderActions>
        <Icon name="arrowLeft" size="large" onClick={onClose} />
      </Slideout.HeaderActions>

      <SlideoutContainer>
        <SlideoutHeader>
          <SlideoutSubHeader>
            <Typography category="title" size="large" weight="bold">
              {drive.name || 'Unnamed Drive'}
            </Typography>
          </SlideoutSubHeader>
          <TextField
            size="medium"
            iconLeft="search"
            placeholder="Browse folders and files"
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </SlideoutHeader>

        {drive.description && (
          <Typography category="body" size="medium" color="body">
            {drive.description}
          </Typography>
        )}

        <SlideoutDrivesContainer>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <CircularProgress size="medium" />
            </div>
          ) : error ? (
            <div style={{ padding: '24px' }}>
              <Typography category="body" size="medium" color="error">
                {error}
              </Typography>
            </div>
          ) : (
            <Scrollbar size="small">
              <DriveDocumentList
                documents={filteredDocuments}
                searchTerm={searchTerm}
                driveName={drive.name}
              />
            </Scrollbar>
          )}
        </SlideoutDrivesContainer>
      </SlideoutContainer>
    </Slideout>
  );
};

export default SlideoutDriveDetail;

